// Subscription status card — ports profile.js _updateSubscriptionUI.
// Four states: token-based (progress), recurring (auto-renew → cancel / or
// expiry), other dated plan, and no-subscription (upsell).

import { useNavigate } from 'react-router-dom'
import type { ProfileStats } from '../../types/api'
import styles from './Settings.module.css'

interface SubscriptionCardProps {
  stats: ProfileStats | null
  onCancel: () => void
  onManage: () => void
  canManage?: boolean
  actionPending?: boolean
}

const PLAN_FALLBACKS = { session: 'Сессия', month: 'Месяц', quarter: 'Квартал' }

function formatDate(unixSeconds: number | null | undefined) {
  if (!unixSeconds) return 'Неизвестно'
  return new Date(unixSeconds * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

function providerLabel(provider: ProfileStats['subscription_provider']) {
  if (provider === 'tochka') return 'Оплата через Точку'
  if (provider === 'rustore') return 'Оплата через RuStore'
  return ''
}

export default function SubscriptionCard({
  stats,
  onCancel,
  onManage,
  canManage = false,
  actionPending = false,
}: SubscriptionCardProps) {
  const navigate = useNavigate()

  if (!stats) return <div className={styles.planCard}><div className={styles.planTop}>Текущий тариф</div><h3>Загрузка...</h3></div>

  const planName =
    stats.plan_description ||
    PLAN_FALLBACKS[stats.subscription_plan as keyof typeof PLAN_FALLBACKS] ||
    stats.subscription_plan ||
    'Standard'

  if (!stats.subscription_status) {
    return (
      <div className={styles.planCard}>
        <div className={styles.planTop}>Текущий тариф</div>
        <h3>Нет подписки</h3>
        <p>Подпишитесь, чтобы разблокировать все возможности</p>
        <button className={styles.primaryBtn} onClick={() => navigate('/pricing')}>Оформить подписку</button>
      </div>
    )
  }

  if (stats.plan_type === 'token_based') {
    const remaining = stats.tokens_remaining ?? 0
    const total = stats.token_limit || 8000
    const percent = total > 0 ? Math.round((remaining / total) * 100) : 0
    return (
      <div className={styles.planCard}>
        <div className={styles.planTop}>Текущий тариф</div>
        <h3>{planName}</h3>
        {providerLabel(stats.subscription_provider) && <p className={styles.muted}>{providerLabel(stats.subscription_provider)}</p>}
        <div className={styles.tokenBar}>
          <div className={styles.tokenFill} style={{ width: `${percent}%` }} />
        </div>
        <span className={styles.tokenText}>
          {remaining.toLocaleString('ru-RU')} / {total.toLocaleString('ru-RU')} токенов
        </span>
      </div>
    )
  }

  if (stats.plan_type === 'recurring') {
    const date = formatDate(stats.next_billing_date)
    const autoRenewal = stats.auto_renewal !== false
    const usesTochkaCancellation = stats.subscription_provider === 'tochka' || stats.subscription_provider === undefined
    return (
      <div className={styles.planCard}>
        <div className={styles.planTop}>Текущий тариф</div>
        <h3>{planName}</h3>
        {providerLabel(stats.subscription_provider) && <p className={styles.muted}>{providerLabel(stats.subscription_provider)}</p>}
        {autoRenewal ? (
          <>
            <p>Следующее списание {date}</p>
            {usesTochkaCancellation && (
              <button className={`${styles.primaryBtn} ${styles.cancelBtn}`} disabled={actionPending} onClick={onCancel}>
                {actionPending ? 'Отмена...' : 'Отменить подписку'}
              </button>
            )}
          </>
        ) : (
          <>
            <p>Действует до {formatDate(stats.subscription_end_date)}</p>
            <p className={styles.muted}>Автопродление отключено</p>
          </>
        )}
        {stats.subscription_provider === 'rustore' && (
          canManage ? (
            <button className={`${styles.primaryBtn} ${styles.cancelBtn}`} disabled={actionPending} onClick={onManage}>
              {actionPending ? 'Управление...' : 'Управлять подпиской'}
            </button>
          ) : (
            <p className={styles.muted}>Управление подпиской доступно в приложении Android</p>
          )
        )}
      </div>
    )
  }

  return (
    <div className={styles.planCard}>
      <div className={styles.planTop}>Текущий тариф</div>
      <h3>{planName}</h3>
      <p>Действует до {formatDate(stats.subscription_end_date)}</p>
    </div>
  )
}
